/**
 * MiniScoreArc — compact half-arc SibaScore speedometer.
 * Renders on a 0..1000 scale. Score number and "/ 1000" caption
 * sit below the arc's baseline. Used on Landing (LiveSibaScore)
 * and Shop product cards.
 *
 * Colors read from the LIVE `sibascore_ranges` admin config via
 * `useSibaScoreConfig()` — no hardcoded band thresholds here. Change the
 * config once and every mini-arc on the platform re-tints on next refresh.
 */
import React, { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useSibaScoreConfig, tierForScore } from "@/hooks/useSibaScoreRanges";

interface Props {
  score: number;
  size?: number;
  showLabel?: boolean;
}

export const MiniScoreArc: React.FC<Props> = ({ score, size = 148, showLabel = true }) => {
  const reduce = useReducedMotion();
  const { ranges, bounds } = useSibaScoreConfig();
  const maxScore = bounds?.max_score || 1000;
  const tier = tierForScore(score, ranges);
  const fillColor = tier.color;

  const stroke = Math.max(8, Math.round(size * 0.08));
  const pad = stroke / 2 + 2;
  const cx = size / 2;
  const cy = size / 2 + 2;
  const r = size / 2 - pad;
  const svgH = size / 2 + stroke + 6;

  const [display, setDisplay] = useState(0);
  const rafRef = useRef<number>(0);
  useEffect(() => {
    if (reduce) { setDisplay(score); return; }
    const start = performance.now();
    const dur = 1200;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(Math.round(eased * score));
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [score, reduce]);

  const filled = Math.max(0, Math.min(1, display / 1000));
  const arcPath = (fromFrac: number, toFrac: number) => {
    const rad = (deg: number) => (deg * Math.PI) / 180;
    const a1 = 180 - fromFrac * 180;
    const a2 = 180 - toFrac * 180;
    const x1 = cx + r * Math.cos(rad(a1));
    const y1 = cy - r * Math.sin(rad(a1));
    const x2 = cx + r * Math.cos(rad(a2));
    const y2 = cy - r * Math.sin(rad(a2));
    const largeArc = Math.abs(a1 - a2) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const numSize = Math.max(18, Math.round(size * 0.20));

  return (
    <div className="relative select-none" style={{ width: size, height: svgH }} data-testid="mini-score-arc">
      <svg width={size} height={svgH} viewBox={`0 0 ${size} ${svgH}`}>
        <path
          d={arcPath(0, 1)}
          stroke="var(--sb-arc-track, rgba(15,23,42,0.08))"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
        />
        {filled > 0 && (
          <path d={arcPath(0, filled)} stroke={fillColor} strokeWidth={stroke} strokeLinecap="round" fill="none" />
        )}
      </svg>
      <div className="absolute inset-x-0 top-1/2 -translate-y-[35%] flex flex-col items-center pointer-events-none">
        <div className="font-serif-display font-bold tabular-nums leading-none text-slate-900 dark:text-white" style={{ fontSize: numSize }}>
          {display}
        </div>
        {showLabel && (
          <div className="text-[9px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold mt-1">
            / {maxScore}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniScoreArc;
